import { useCallback, useMemo } from 'react';
import compact from 'lodash/compact';

import { FormContextValues, useTranslate } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { patientTranslationKeys, personalDataTranslationKeys } from '../../../translations';
import { PersonalIdentifierType } from '../../common.types';
import { PersonalDataFormBeanPart } from '../../PersonalDataFormSection';

type PersonalIdentifierTypeFieldProps = Pick<
   FormContextValues<PersonalDataFormBeanPart>,
   'register' | 'getValues' | 'reset'
> & {
   readonly withGuardians: boolean | undefined;
};

export type PatientSelectIdentifierType = {
   name: string;
   id: PersonalIdentifierType;
};

export const peselAsSelectItem: PatientSelectIdentifierType = {
   name: personalDataTranslationKeys.identifierType.typeName.Pesel,
   id: PersonalIdentifierType.PESEL,
};

export const guardianPeselAsSelectItem: PatientSelectIdentifierType = {
   name: personalDataTranslationKeys.identifierType.typeName.GuardianPesel,
   id: PersonalIdentifierType.GUARDIAN_PESEL,
};

export const passportAsSelectItem: PatientSelectIdentifierType = {
   name: personalDataTranslationKeys.identifierType.typeName.Passport,
   id: PersonalIdentifierType.PASSPORT,
};

export const nationalIdentificationNumberAsSelectItem: PatientSelectIdentifierType = {
   name: personalDataTranslationKeys.identifierType.typeName.NationalIdentificationNumber,
   id: PersonalIdentifierType.NATIONAL_IDENTIFICATION_NUMBER,
};

export const medicalRecordNumberAsSelectItem: PatientSelectIdentifierType = {
   name: personalDataTranslationKeys.identifierType.typeName.MedicalRecordNumber,
   id: PersonalIdentifierType.MEDICAL_RECORD_NUMBER,
};

export const getDefaultPatientIdentifierType = (): PatientSelectIdentifierType => {
   return protectFeature('usingMedicalRecordNumbers', medicalRecordNumberAsSelectItem, peselAsSelectItem);
};

const useIdentifierTypes = (withGuardians: boolean | undefined): PatientSelectIdentifierType[] => {
   return useMemo(() => {
      if (!withGuardians) {
         return compact([
            protectFeature('usingMedicalRecordNumbers', medicalRecordNumberAsSelectItem, null),
            peselAsSelectItem,
            passportAsSelectItem,
            nationalIdentificationNumberAsSelectItem,
         ]);
      }
      return compact([
         protectFeature('usingMedicalRecordNumbers', medicalRecordNumberAsSelectItem, null),
         peselAsSelectItem,
         guardianPeselAsSelectItem,
         passportAsSelectItem,
         nationalIdentificationNumberAsSelectItem,
      ]);
   }, [withGuardians]);
};

export const PersonalIdentifierTypeField = (props: PersonalIdentifierTypeFieldProps): JSX.Element => {
   const translate = useTranslate();
   const triggerLabelExtractor = (item?: PatientSelectIdentifierType): string => {
      return translate(item?.name ?? '');
   };
   const identifierTypes = useIdentifierTypes(props.withGuardians);

   const resetFormData = useCallback(() => {
      props.reset({
         ...props.getValues(),
         pesel: null,
         guardianPesel: null,
         passport: null,
         passportCountry: null,
         passportValidTo: null,
         nationalIdentificationNumber: null,
         nationalIdentificationNumberCountry: null,
         medicalRecordNumber: null,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.reset, props.getValues]);

   return (
      <Select<PatientSelectIdentifierType>
         testId={'personalIdentifierType'}
         className={'personalIdentifierType'}
         label={patientTranslationKeys.personalIdentifierType}
         triggerLabelExtractor={triggerLabelExtractor}
         controller={props.register('personalIdentifierType', {
            required: true,
         })}
         onSelect={resetFormData}
      >
         <Select.List items={identifierTypes} keyExtractor={'id'}>
            {(identifierType: PatientSelectIdentifierType) => (
               <Select.Item item={identifierType} testId={identifierType.id} label={identifierType.name} />
            )}
         </Select.List>
      </Select>
   );
};
